import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { MATCAP_GAIN, studio, smooth01, clamp01, encoder, sdRoundBox } from './lib/matcap.js';
import { generateCracks, cracksWorkerSource, seedOf, CRACK_N, CRACK_BAND } from './lib/kintsugi-cracks.js';

// Contrat du rig partagé : interdit. Le bol n'a d'autre lumière que ses
// matcaps.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'kintsugi',
  family: 'objects',
  danceability: 0.24,
  requiresTier: 0,
  energyLevel: 0.15,
  bpmMin: 40,
  bpmMax: 110,
  movement: 0.10,
  tonality: 0.90,
  enabled: true
};

// KINTSUGI : un bol de céramique noire, fêlé, vu de dessus ; au fil du
// morceau, de l'or liquide coule dans les fêlures et les répare. Ambient,
// néo-classique, piano, jazz lent, musique japonaise.
//
// LE BOL est une surface de RÉVOLUTION (fond parabolique, lèvre arrondie en
// terre brute, paroi extérieure) dont les normales sont ANALYTIQUES (la
// dérivée du profil, tournée autour de l'axe). Vu de dessus, incliné de 10°,
// le fond sous la pochette DOM, 80 % de la hauteur d'écran de diamètre (moins
// si la marge de 5 % ou les zones d'exclusion l'imposent). Il tourne d'un
// tour sur la durée du morceau. Céramique : matcap LAQUE NOIRE générée (le
// corps à 0,008, un reflet large et doux, un second discret), une glaçure à
// peine bruitée en brillance ; la lèvre est mate. Rendu dans une cible MSAA
// privée (le contour du bol), montrée 1:1.
//
// LES FÊLURES : générées une fois par morceau dans un Web Worker
// (lib/kintsugi-cracks.js, R17), graine = le titre. Deux textures : la
// distance à la fêlure (filtrée, R2) et l'ARRIVÉE de l'or (plus court chemin
// depuis un point du bord). Le shader dessine tout par distance en pixels.
//   - non remplie : un creux sombre de 1,4 px, un liseré de lumière du côté
//     éclairé (la pente du champ de distance tournée vers la lumière) ;
//   - remplie : l'or, 2 à 4 px d'épaisseur irrégulière (secondaires 1,2 à
//     2,2), matcap OR ; sa normale se bombe à travers la ligne, si bien que
//     les reflets glissent quand le bol tourne ;
//   - la TÊTE : l'arrivée la plus récente, sur 0,03 rayon, plus large et plus
//     brillante, la pointe arrondie.
//
// LE TEMPS : F, la longueur de chemin remplie, avance à la vitesse qu'il faut
// pour finir à 95 % du morceau, modulée par l'intensité lissée sur 8 mesures
// (x 0,6 à x 1,4), recalculée à chaque frame : le total arrive toujours à
// 100 %. Un saut dans le morceau (au-delà de 8 % d'écart) recale F. Aucune
// impulsion (R9, danceability 0,24). Au drop : une lueur chaude parcourt
// l'or déjà posé en 4 mesures.
//
// DEBUG : ?kintsugip=<0..1> (progression forcée), ?kintsugiseed=<n>,
// ?kintsugiprobe=1 (window.__kintsugi).

const TAU = Math.PI * 2;
const R_IN = 9.3;                 // the glaze's edge (the cracks' disk)
const DEPTH = 4.2;
const LIP_R = 0.36;
const FOOT = [6.4, -5.1];
const TILT = 10 * Math.PI / 180, FOV = 26;
const DIAMETER = 0.80;            // of the screen height
const EDGE_MARGIN = 0.10, DRIFT_MARGIN = 0.02;
const EXCLUSIONS = [['tr', 0.22, 0.12], ['tl', 0.08, 0.12], ['br', 0.22, 0.10]];
const FILL_END = 0.95;
const SEEK_BAND = 0.08;
const HEAD = 0.03;                // the head's length, disk units
const DROP_BARS = 4, DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const FADE_S = 1.5;
const NO_DURATION_S = 300;

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- le profil du bol ----------------------------------------------------------
// [r, y, nr, ny, zone]: 0 glaze inside, 1 raw clay lip, 2 glaze outside
export function bowlProfile() {
  const P = [];
  const NI = 72;
  for (let k = 0; k <= NI; k++) {
    const t = k / NI, r = R_IN * Math.pow(t, 0.85);
    const slope = 2 * DEPTH * r / (R_IN * R_IN);            // y = -D (1 - r²/R²)
    const l = Math.hypot(slope, 1);
    P.push([r, -DEPTH * (1 - (r * r) / (R_IN * R_IN)), -slope / l, 1 / l, 0]);
  }
  // the lip: an arc that starts tangent to the inside (same normal)
  const n0 = P[P.length - 1];
  const phi0 = Math.atan2(n0[3], n0[2]);
  const cx = R_IN - LIP_R * Math.cos(phi0), cy = 0 - LIP_R * Math.sin(phi0);
  const phi1 = -0.55;
  const NL = 14;
  for (let k = 1; k <= NL; k++) {
    const phi = phi0 + (phi1 - phi0) * k / NL;
    P.push([cx + LIP_R * Math.cos(phi), cy + LIP_R * Math.sin(phi), Math.cos(phi), Math.sin(phi), 1]);
  }
  // the outside: a straight wall to the foot, its normal perpendicular
  const last = P[P.length - 1];
  const dx = FOOT[0] - last[0], dy = FOOT[1] - last[1], dl = Math.hypot(dx, dy);
  for (let k = 1; k <= 8; k++) P.push([last[0] + dx * k / 8, last[1] + dy * k / 8, -dy / dl, dx / dl, 2]);
  return P;
}
const PROFILE_PTS = bowlProfile();
export const RIM_R = Math.max(...PROFILE_PTS.map((p) => p[0]));

function bowlGeometry(segments) {
  const P = PROFILE_PTS, NA = segments;
  const nv = P.length * (NA + 1);
  const pos = new Float32Array(nv * 3), nrm = new Float32Array(nv * 3), disk = new Float32Array(nv * 2), zone = new Float32Array(nv);
  let v = 0;
  for (let i = 0; i < P.length; i++) {
    const [r, y, nr, ny, z] = P[i];
    for (let j = 0; j <= NA; j++, v++) {
      const th = TAU * j / NA, c = Math.cos(th), s = Math.sin(th);
      pos[v * 3] = r * c; pos[v * 3 + 1] = y; pos[v * 3 + 2] = r * s;
      nrm[v * 3] = nr * c; nrm[v * 3 + 1] = ny; nrm[v * 3 + 2] = nr * s;
      disk[v * 2] = r * c / R_IN; disk[v * 2 + 1] = r * s / R_IN;
      zone[v] = z;
    }
  }
  const idx = [];
  for (let i = 0; i + 1 < P.length; i++) for (let j = 0; j < NA; j++) {
    const a = i * (NA + 1) + j, b = a + 1, c = a + NA + 1, d = c + 1;
    idx.push(a, c, b, b, c, d);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  g.setAttribute('aDisk', new THREE.BufferAttribute(disk, 2));
  g.setAttribute('aZone', new THREE.BufferAttribute(zone, 1));
  g.setIndex(idx);
  return g;
}

// --- les matcaps -----------------------------------------------------------------
// LAQUE NOIRE: the body almost black (0.008), one LARGE soft light box (edge
// over ~30 px of matcap) at 0.55, a second faint one, a whisper of Fresnel.
// OR: amber body in the light, dark amber in the shadow, two warm boxes
// with sharp edges (the glints that bloom a little).
const LACQUER_BOXES = [{ cx: -0.34, cy: 0.46, hw: 0.26, hh: 0.10, rr: 0.09 }, { cx: 0.42, cy: -0.58, hw: 0.16, hh: 0.05, rr: 0.04 }];
export function makeMatcap(kind, n) {
  const N = n || 256, data = new Uint8Array(N * N * 4), px = 2 / N;
  for (let j = 0; j < N; j++) {
    const y = ((j + 0.5) / N) * 2 - 1;
    for (let i = 0; i < N; i++) {
      const x = ((i + 0.5) / N) * 2 - 1, o = (j * N + i) * 4;
      let r, g, b;
      if (kind === 'lacquer') {
        const st = studio(x, y, px, { edgePx: 30, horizonPx: 20, boxes: [] });
        const body = 0.0018 + 0.0035 * st.lit;     // black lacquer: ~0.04 in sRGB
        // the two boxes apart: the large soft one at 0.55, the small at 0.18
        const e = 22 * px;
        const box = 0.24 * smooth01(e, -e, sdRoundBox(x, y, LACQUER_BOXES[0])) + 0.08 * smooth01(e, -e, sdRoundBox(x, y, LACQUER_BOXES[1]));
        const v = body + box + 0.012 * st.fres;
        r = v * 1.0; g = v * 0.985; b = v * 0.97;
      } else {
        const st = studio(x, y, px, { edgePx: 3 });
        const t = smooth01(-0.6, 0.9, st.ry * 0.6 - x * 0.4);
        const body = [0.07 + 0.55 * t, 0.035 + 0.33 * t, 0.008 + 0.09 * t];
        const lit = 0.25 + 0.75 * st.lit;
        r = body[0] * lit + 1.15 * st.box; g = body[1] * lit + 0.95 * st.box; b = body[2] * lit + 0.55 * st.box;
        r += 0.18 * st.fres; g += 0.11 * st.fres; b += 0.03 * st.fres;
      }
      data[o] = encoder(r); data[o + 1] = encoder(g); data[o + 2] = encoder(b); data[o + 3] = 255;
    }
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.needsUpdate = true;
  return t;
}

// --- GLSL -----------------------------------------------------------------------
const BOWL_VERTEX = `
  precision highp float;
  attribute vec2 aDisk;
  attribute float aZone;
  varying vec3 vViewN;
  varying vec2 vDisk;
  varying float vZone;
  void main() {
    vViewN = normalize(normalMatrix * normal);
    vDisk = aDisk;
    vZone = aZone;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const BOWL_FRAGMENT = `
  precision highp float;
  uniform sampler2D uLacquer;
  uniform sampler2D uGold;
  uniform sampler2D uDist;
  uniform sampler2D uArr;
  uniform float uHasCracks;
  uniform float uCrackAlpha;
  uniform float uF;          // the filled path length (disk units)
  uniform float uMaxArr;
  uniform float uSweep;      // the drop's glow front (disk units), < 0 off
  uniform float uSweepAmp;
  uniform float uSqrtOut;
  varying vec3 vViewN;
  varying vec2 vDisk;
  varying float vZone;
  #define OUT(c) gl_FragColor = vec4(uSqrtOut > 0.5 ? sqrt(max((c), vec3(0.0))) : (c), 1.0)
  vec3 mcap(sampler2D t, vec3 n) {
    vec3 c = texture2D(t, n.xy * 0.49 + 0.5).rgb;
    return c * c * ${MATCAP_GAIN.toFixed(2)};
  }
  // value noise on a BOUNDED lattice (fp16 on the TV: never hash big inputs)
  float hash(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
  }
  void main() {
    vec3 n = normalize(vViewN);
    if (n.z < 0.0) n = normalize(vec3(n.xy, 0.0) + vec3(0.0, 0.0, 0.02));
    // derivatives first, in uniform control flow
    float pxw = length(fwidth(vDisk)) * 0.7071;               // disk units per screen px
    vec2 uv = vDisk * 0.5 + 0.5;
    vec4 D = texture2D(uDist, uv);
    vec4 A = texture2D(uArr, uv);
    float dU = D.r * ${(CRACK_BAND * 2 / CRACK_N).toFixed(6)};   // distance, disk units
    vec2 g = vec2(dFdx(dU), dFdy(dU));
    float glaze = vnoise(vDisk * 70.0) * 0.6 + vnoise(vDisk * 190.0) * 0.4;
    // the zones blend over one pixel (a colour edge INSIDE the mesh, which
    // MSAA does not smooth): 0 glaze inside, 1 clay lip, 2 glaze outside
    float wz = fwidth(vZone) + 1e-4;
    float kClay = smoothstep(0.5 - wz, 0.5 + wz, vZone) * (1.0 - smoothstep(1.5 - wz, 1.5 + wz, vZone));
    float kInside = 1.0 - smoothstep(0.5 - wz, 0.5 + wz, vZone);
    float l = 0.30 + 0.70 * max(dot(n, normalize(vec3(-0.45, 0.65, 0.62))), 0.0);
    vec3 clay = vec3(0.060, 0.044, 0.032) * l * (0.8 + 0.4 * glaze);
    // lacquer: the matcap, the glaze's gloss varying a little
    vec3 c = mcap(uLacquer, n);
    c = vec3(0.0018) + (c - vec3(0.0018)) * (0.80 + 0.35 * glaze);
    if (uHasCracks > 0.5 && uCrackAlpha > 0.001) {
      float dpx = dU / max(pxw, 1e-6);
      float a = (A.r * 65280.0 + A.g * 255.0) / 65535.0 * uMaxArr;
      float sec = step(0.5, D.g);
      float lead = uF - a;                                    // > 0: gold here
      vec2 gd = normalize(g + vec2(1e-7));
      // the empty crack: a dark groove, a light rim on the side facing the light
      float wC = mix(1.4, 1.0, sec);
      float groove = 1.0 - smoothstep(wC * 0.5 - 0.5, wC * 0.5 + 0.5, dpx);
      float lit = max(0.0, dot(gd, normalize(vec2(-0.55, 0.83))));
      float rim = (1.0 - smoothstep(0.0, 0.9, abs(dpx - wC * 0.5 - 0.9))) * lit;
      vec3 empty = c * (1.0 - 0.92 * groove) + vec3(0.045, 0.042, 0.04) * rim;
      // the gold: 2 to 4 px, irregular; the head wider, brighter, its tip round
      float wob = 0.5 + 0.5 * sin(6.2831853 * A.b);
      float head = (1.0 - smoothstep(0.0, ${HEAD.toFixed(3)}, lead)) * step(0.0, lead);
      float tip = sqrt(clamp(lead / 0.007, 0.0, 1.0));
      float wG = mix(2.0, 4.0, wob) * mix(1.0, 0.55, sec) * (1.0 + 0.30 * head) * tip;
      float gm = (1.0 - smoothstep(wG * 0.5 - 0.6, wG * 0.5 + 0.6, dpx)) * step(0.0, lead);
      // a bead: the normal leans outward across the line, so the
      // reflections slide as the bowl turns
      float across = clamp(dpx / max(wG * 0.5, 0.5), 0.0, 1.0);
      vec3 ng = normalize(n + vec3(gd * across * 1.1, 0.0));
      vec3 gold = mcap(uGold, ng) * (1.0 + 0.55 * head);
      // the drop: a warm glow travelling along the gold already laid
      if (uSweep >= 0.0) {
        float sw = exp(-pow((uSweep - a) / 0.22, 2.0)) * step(0.0, lead) * uSweepAmp;
        gold *= 1.0 + 0.8 * sw;
        empty += vec3(0.30, 0.17, 0.05) * sw * exp(-max(dpx - wG * 0.5, 0.0) / 3.0) * 0.25;
      }
      vec3 crackC = mix(empty, gold, gm);
      c = mix(c, crackC, uCrackAlpha * kInside);
    }
    OUT(mix(c, clay, kClay));
  }
`;

const DISPLAY_VERTEX = `
  precision highp float;
  void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const DISPLAY_FRAGMENT = `
  precision highp float;
  uniform sampler2D uRoom;
  uniform vec2 uSize;
  uniform float uSqrtOut;
  void main() {
    vec3 c = texture2D(uRoom, gl_FragCoord.xy / uSize).rgb;
    if (uSqrtOut > 0.5) c *= c;
    gl_FragColor = vec4(c, 1.0);
  }
`;

// --- le cadrage -------------------------------------------------------------------
export function forbiddenOut(x, y, aspect) {
  const A = aspect, m = EDGE_MARGIN + DRIFT_MARGIN;
  if (Math.abs(x) > A - m || Math.abs(y) > 1 - m) return true;
  for (const [corner, fw, fh] of EXCLUSIONS) {
    const w = fw * 2 * A, h = fh * 2;
    const x0 = corner[1] === 'r' ? A - w : -A, x1 = corner[1] === 'r' ? A : -A + w;
    const y0 = corner[0] === 't' ? 1 - h : -1, y1 = corner[0] === 't' ? 1 : -1 + h;
    if (x > x0 && x < x1 && y > y0 && y < y1) return true;
  }
  return false;
}
const _cam = new THREE.PerspectiveCamera(FOV, 1.78, 1, 5000);
const _v = new THREE.Vector3();
const BOTTOM = new THREE.Vector3(0, -DEPTH, 0);
const RIM_PTS = [];
for (let k = 0; k < 96; k++) {
  const a = TAU * k / 96;
  RIM_PTS.push(new THREE.Vector3(RIM_R * Math.cos(a), LIP_R * 0.3, RIM_R * Math.sin(a)));
  RIM_PTS.push(new THREE.Vector3((RIM_R - 0.2) * Math.cos(a), -1.0, (RIM_R - 0.2) * Math.sin(a)));
}
function placeCamera(cam, D, tz, aspect) {
  cam.fov = FOV; cam.aspect = aspect; cam.near = Math.max(1, D * 0.3); cam.far = D * 3;
  cam.zoom = 1; cam.filmOffset = 0;
  cam.up.set(0, 1, 0);
  cam.position.set(0, D * Math.cos(TILT), tz + D * Math.sin(TILT));
  cam.lookAt(0, 0, tz);
  cam.updateProjectionMatrix();
  cam.updateMatrixWorld(true);
}
// D and tz: the bowl's bottom under the cover's centre, its rim 80 % of the
// screen tall, or smaller if the margins and exclusions require it
export function fitCamera(aspect, coverY) {
  const aimFor = (D) => {
    let lo = -100, hi = 100;
    for (let it = 0; it < 40; it++) {
      const m = (lo + hi) / 2;
      placeCamera(_cam, D, m, aspect);
      if (_v.copy(BOTTOM).project(_cam).y > coverY) hi = m; else lo = m;
    }
    return (lo + hi) / 2;
  };
  const measure = (D) => {
    const tz = aimFor(D);
    placeCamera(_cam, D, tz, aspect);
    let y0 = 9, y1 = -9, out = false;
    for (const p of RIM_PTS) {
      _v.copy(p).project(_cam);
      y0 = Math.min(y0, _v.y); y1 = Math.max(y1, _v.y);
      if (forbiddenOut(_v.x * aspect, _v.y, aspect)) out = true;
    }
    return { tz, height: (y1 - y0) / 2, out };
  };
  // the distance that gives the wanted diameter
  let lo = 5, hi = 2000;
  for (let it = 0; it < 40; it++) { const m = (lo + hi) / 2; if (measure(m).height > DIAMETER) lo = m; else hi = m; }
  let D = hi;
  // then back off until it fits
  for (let it = 0; it < 200 && measure(D).out; it++) D *= 1.01;
  const m = measure(D);
  return { D, tz: m.tz, height: m.height };
}

// --- le worker (un seul pour la page, réutilisé) ---------------------------------
let _worker = null, _workerFailed = false, _nextId = 0;
const _waiting = new Map();
function sharedWorker() {
  if (_worker || _workerFailed) return _worker;
  if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') { _workerFailed = true; return null; }
  try {
    const url = URL.createObjectURL(new Blob([cracksWorkerSource()], { type: 'text/javascript' }));
    _worker = new Worker(url);
    _worker.onmessage = (ev) => { const cb = _waiting.get(ev.data.id); if (cb) { _waiting.delete(ev.data.id); cb(ev.data); } };
    _worker.onerror = () => { for (const [id, cb] of _waiting) cb({ id, error: 'worker' }); _waiting.clear(); };
  } catch (e) { _workerFailed = true; _worker = null; }
  return _worker;
}

export class KintsugiVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    const fp = parseFloat(urlParam('kintsugip'));
    this._forceP = Number.isFinite(fp) ? clamp01(fp) : null;
    const fs = parseInt(urlParam('kintsugiseed'), 10);
    this._forceSeed = Number.isFinite(fs) ? fs : null;
    this._post = { bloomStrength: 0.14, bloomThreshold: 0.78, grain: 0.008, vignette: 0.35, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('kintsugiprobe') === '1' && typeof window !== 'undefined') window.__kintsugi = this;
  }

  get postProcessing() { return this._post; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    sharedWorker();
    this._room = new THREE.Scene();
    this._lacquer = makeMatcap('lacquer');
    this._gold = makeMatcap('gold');
    this._blank = new THREE.DataTexture(new Uint8Array([255, 0, 0, 255]), 1, 1);
    this._blank.needsUpdate = true;
    this._u = {
      uLacquer: { value: this._lacquer }, uGold: { value: this._gold },
      uDist: { value: this._blank }, uArr: { value: this._blank },
      uHasCracks: { value: 0 }, uCrackAlpha: { value: 0 }, uF: { value: 0 }, uMaxArr: { value: 1 },
      uSweep: { value: -1 }, uSweepAmp: { value: 0 }, uSqrtOut: { value: this._tier >= 2 ? 0 : 1 }
    };
    this._bowl = new THREE.Mesh(bowlGeometry(this._tier >= 1 ? 192 : 128), new THREE.ShaderMaterial({
      uniforms: this._u, vertexShader: BOWL_VERTEX, fragmentShader: BOWL_FRAGMENT, side: THREE.DoubleSide
    }));
    this._bowl.frustumCulled = false;
    this._room.add(this._bowl);

    this._rt = null;
    this._dispU = { uRoom: { value: null }, uSize: { value: new THREE.Vector2(1, 1) }, uSqrtOut: this._u.uSqrtOut };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial({
      uniforms: this._dispU, vertexShader: DISPLAY_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false
    }));
    this._display.frustumCulled = false;
    this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._renderRoom(renderer); };
    scene.add(this._display);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._key = undefined;
    this._req = null;
    this._data = null;          // { dist, arr textures, maxArr }
    this._pending = null;       // new cracks waiting for the fade-out
    this._fade = 0;             // crack visibility
    this._fadeDir = 1;
    this._F = 0;
    this._energy = null; this._eMean = null;
    this._angle = 0;
    this._p = 0; this._pTarget = 0;
    this._sweep = -1; this._sweepRate = 0;
    this._modePrev = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._fitKey = ''; this._fit = null;
    this._log = [];
    this._placeCamera();
  }

  _note(what, extra) { this._log.push(Object.assign({ t: +this._time.toFixed(2), what }, extra || {})); if (this._log.length > 200) this._log.shift(); }

  _placeCamera() {
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    const cy = safeZoneUniforms.uStageCenter.value.y;   // R19: the bowl sits on the stage centre, not the cover
    const key = aspect.toFixed(3) + ',' + cy.toFixed(3);
    if (key !== this._fitKey) { this._fitKey = key; this._fit = fitCamera(aspect, cy); }
    placeCamera(this._camera, this._fit.D, this._fit.tz, this._camera.aspect || aspect);
  }

  // the cracks: one request per track (the title is the seed)
  _request(key) {
    const seed = this._forceSeed != null ? this._forceSeed : seedOf(key);
    const id = ++_nextId;
    this._req = { id, seed };
    const w = sharedWorker();
    const done = (m) => this._onCracks(m, id, seed);
    if (w) { _waiting.set(id, done); w.postMessage({ id, seed, n: CRACK_N }); }
    else {
      const t0 = performance.now();
      const res = generateCracks(seed, CRACK_N); res.ms = performance.now() - t0;
      done({ id, res });
    }
  }
  _onCracks(m, id, seed) {
    if (!this._scene || !this._req || this._req.id !== id) return;
    if (m.error) {
      // the worker broke: here, once
      const res = generateCracks(seed, CRACK_N);
      m = { id, res };
    }
    this._req = null;
    const r = m.res;
    const tex = (data, filter) => {
      const t = new THREE.DataTexture(data, r.n, r.n, THREE.RGBAFormat, THREE.UnsignedByteType);
      t.colorSpace = THREE.NoColorSpace; t.minFilter = filter; t.magFilter = filter;
      t.generateMipmaps = false; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.needsUpdate = true;
      return t;
    };
    const data = { dist: tex(r.dist, THREE.LinearFilter), arr: tex(r.arr, THREE.NearestFilter), maxArr: r.maxArr };
    this._note('cracks', { ms: r.ms != null ? +r.ms.toFixed(1) : null, seed, stats: r.stats });
    if (!this._data) { this._install(data); this._fadeDir = 1; }
    else { this._pending = data; this._fadeDir = -1; }
  }
  _install(data) {
    if (this._data) { this._data.dist.dispose(); this._data.arr.dispose(); }
    this._data = data;
    this._u.uDist.value = data.dist; this._u.uArr.value = data.arr;
    this._u.uMaxArr.value = data.maxArr; this._u.uHasCracks.value = 1;
    // a new track: the gold where this track's position says it should be
    this._F = clamp01(this._p / FILL_END) * data.maxArr;
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._placeCamera();
    this._paused = profile.playbackState !== 'PLAYING';
    // the track: its cracks (asked even when paused, so they are ready)
    const key = ((profile.title || '') + '|' + (profile.artist || '')) || String(profile.trackId || '');
    if (key !== this._key) { this._key = key; this._request(key); }
    if (this._paused) return;           // frozen: the gold stops, the bowl stops
    this._time += dt;

    // THE POSITION in the track, smooth (the rotation and the schedule)
    const dur = profile.duration > 0 ? profile.duration : 0;
    const pos = profile.estimatedPosition != null ? profile.estimatedPosition : null;
    let pT;
    if (this._forceP != null) pT = this._forceP;
    else if (dur && pos != null) pT = clamp01(pos / dur);
    else if (dur && profile.progress != null) pT = clamp01(profile.progress);
    else pT = (this._pTarget + dt / NO_DURATION_S) % 1;
    this._pTarget = pT;
    // a jump (a seek, a new track) eases over ~2 s; otherwise follow exactly
    if (Math.abs(pT - this._p) > 0.02) this._p += (pT - this._p) * Math.min(1, dt / 0.6);
    else this._p = pT;
    this._angle = TAU * this._p;
    this._bowl.rotation.y = -this._angle;

    // the crack fade (track changes)
    if (this._fadeDir < 0) {
      this._fade = Math.max(0, this._fade - dt / FADE_S);
      if (this._fade <= 0 && this._pending) { this._install(this._pending); this._pending = null; this._fadeDir = 1; }
    } else this._fade = Math.min(1, this._fade + dt / FADE_S);
    this._u.uCrackAlpha.value = this._data ? smooth01(0, 1, this._fade) : 0;

    // THE GOLD: the speed that ends at 95 % of the track, times the energy
    // smoothed over 8 bars
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 80;
    const barS = 4 * 60 / bpm;
    const dir = profile.director || {};
    const e = dir.intensity != null ? clamp01(dir.intensity) : 0.5;
    this._energy = this._energy == null ? e : this._energy + (e - this._energy) * Math.min(1, dt / (8 * barS));
    // the track's own mean energy (since its start): the speed is RELATIVE to
    // it, so a calm track is not late and an intense one not early
    this._eMean = this._eMean == null ? e : this._eMean + (e - this._eMean) * Math.min(1, dt / 60);
    if (this._data) {
      const M = this._data.maxArr;
      const target = clamp01(this._p / FILL_END) * M;
      if (this._forceP != null) this._F = target;
      else {
        const remaining = M - this._F;
        const secsLeft = dur ? Math.max(0.5, (FILL_END - this._p) * dur) : Math.max(0.5, (FILL_END - this._p) * NO_DURATION_S);
        // x 0.6 to x 1.4 around the mean, back to x 1 over the last tenth so
        // the gold lands exactly at 95 %
        let k = (0.6 + 0.8 * clamp01(this._energy)) / (0.6 + 0.8 * clamp01(this._eMean));
        k = Math.max(0.6, Math.min(1.4, k));
        k += (1 - k) * smooth01(FILL_END - 0.12, FILL_END - 0.02, this._p);
        if (this._p < FILL_END) this._F = Math.min(M, this._F + Math.max(0, remaining) / secsLeft * k * dt);
        else this._F = Math.min(M, this._F + M * dt / 4);      // the last few seconds: finish
        // a seek: never further than 8 % from where the track is
        const band = SEEK_BAND * M;
        if (this._F < target - band) this._F = target - band;
        if (this._F > target + band) this._F = target + band;
      }
      this._u.uF.value = this._F;
    }

    // THE DROP: a warm glow along the laid gold, in 4 bars
    const mode = dir.mode || null;
    const drop = this._tickDrops(profile) || (mode === 'drop' && this._modePrev != null && this._modePrev !== 'drop');
    this._modePrev = mode;
    if (drop && this._sweep < 0 && this._data && this._F > 0.05) {
      this._sweep = 0; this._sweepRate = (this._F + 0.5) / (DROP_BARS * barS);
      this._note('drop');
    }
    if (this._sweep >= 0) {
      this._sweep += this._sweepRate * dt;
      const end = this._F + 0.5;
      this._u.uSweepAmp.value = smooth01(0, 0.3, this._sweep) * (1 - smooth01(end - 0.5, end, this._sweep));
      this._u.uSweep.value = this._sweep;
      if (this._sweep >= end) { this._sweep = -1; this._u.uSweep.value = -1; }
    }
  }

  // the bowl into a 4x MSAA target sized on the composer's target (R5), shown 1:1
  _renderRoom(renderer) {
    const cur = renderer.getRenderTarget();
    const size = cur ? _size.set(cur.width, cur.height) : renderer.getDrawingBufferSize(_size);
    const w = Math.max(2, Math.round(size.x)), h = Math.max(2, Math.round(size.y));
    if (!this._rt || this._rt.width !== w || this._rt.height !== h) {
      if (this._rt) this._rt.dispose();
      this._rt = new THREE.WebGLRenderTarget(w, h, {
        samples: 4, type: this._tier >= 2 ? THREE.HalfFloatType : THREE.UnsignedByteType,
        minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: true
      });
      this._dispU.uSize.value.set(w, h);
    }
    const xr = renderer.xr.enabled, autoClear = renderer.autoClear;
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    renderer.xr.enabled = false; renderer.autoClear = true;
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this._rt);
    renderer.render(this._room, this._camera);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.autoClear = autoClear; renderer.xr.enabled = xr;
    this._dispU.uRoom.value = this._rt.texture;
  }

  dispose() {
    if (this._bowl) { this._room.remove(this._bowl); this._bowl.geometry.dispose(); this._bowl.material.dispose(); }
    if (this._display) { this._display.onBeforeRender = null; if (this._scene) this._scene.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._rt) { this._rt.dispose(); this._rt = null; }
    for (const t of [this._lacquer, this._gold, this._blank]) if (t) t.dispose();
    if (this._data) { this._data.dist.dispose(); this._data.arr.dispose(); this._data = null; }
    if (this._pending) { this._pending.dist.dispose(); this._pending.arr.dispose(); this._pending = null; }
    this._req = null;
    if (typeof window !== 'undefined' && window.__kintsugi === this) window.__kintsugi = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
